/**
 * Work order lifecycle transitions (reopen/close/delete/approve/route).
 *
 * Extracted verbatim from workOrdersService.ts — functions are invoked with
 * `this` bound to the WorkOrdersService instance (WorkOrdersCtx).
 */
/**
 * Reopen work order.
 *
 * options.acknowledgeImpacts confirms awareness of accounting impacts when
 * the work order is on a synced provider invoice (the backend additionally
 * requires the workorders:reopen:override-accounting permission). Errors
 * carry the backend body on `errorBody` (like createCredit/createWorkOrder)
 * so parseReopenError can read the structured code
 * (REOPEN_WINDOW_EXPIRED / REOPEN_ACCOUNTING_BLOCKED) and caller_can_override
 * — the ApiClient flattens `error` to the prose message alone, so the body is
 * the only place those fields survive.
 */
export async function reopenWorkOrder(workOrderId, options) {
    try {
        const query = options?.acknowledgeImpacts ? '?acknowledge_impacts=true' : '';
        const response = await this.client.post(`/work-orders/${workOrderId}/reopen${query}`, {});
        if (!response.success) {
            const err = new Error(response.error || 'Failed to reopen work order');
            if (response.errorBody)
                err.errorBody = response.errorBody;
            throw err;
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Reopen work order error:', error);
        throw error;
    }
}
/**
 * Close work order (after it was reopened and all services are completed)
 */
export async function closeWorkOrder(workOrderId) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/close`, {});
        if (!response.success) {
            throw new Error(response.error || 'Failed to close work order');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Close work order error:', error);
        throw error;
    }
}
/**
 * Get the impact summary for deleting a work order (accounting sync, invoice batches, etc.)
 */
export async function getDeleteImpact(workOrderId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/delete-impact`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to get delete impact');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Get delete impact error:', error);
        throw error;
    }
}
/**
 * Delete work order (soft delete)
 */
export async function deleteWorkOrder(workOrderId, acknowledgeImpacts = false) {
    try {
        const params = acknowledgeImpacts ? { acknowledge_impacts: 'true' } : undefined;
        const response = await this.client.delete(`/work-orders/${workOrderId}`, params);
        if (!response.success) {
            throw new Error(response.error || 'Failed to delete work order');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Delete work order error:', error);
        throw error;
    }
}
/**
 * Undelete work order (restore soft deleted)
 */
export async function undeleteWorkOrder(workOrderId) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/undelete`, {});
        if (!response.success) {
            throw new Error(response.error || 'Failed to undelete work order');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Undelete work order error:', error);
        throw error;
    }
}
/**
 * Approve work order
 */
export async function approveWorkOrder(workOrderId) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/approve`, {});
        if (!response.success) {
            throw new Error(response.error || 'Failed to approve work order');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Approve work order error:', error);
        throw error;
    }
}
/**
 * Request re-approval for an in-progress work order
 */
export async function requestReapproval(workOrderId, note) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/request-reapproval`, {
            note: note || '',
        });
        if (!response.success) {
            throw new Error(response.error || 'Failed to request re-approval');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Request reapproval error:', error);
        throw error;
    }
}
/**
 * Get available departments for routing a work order
 * Returns list of compatible departments with service counts
 */
export async function getAvailableDepartments(workOrderId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/available-departments`);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to get available departments');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Get available departments error:', error);
        throw error;
    }
}
/**
 * Route work order to a different department
 * Transfers all open services from current department to target department
 */
export async function routeToDepartment(workOrderId, departmentId) {
    try {
        const response = await this.client.post(`/work-orders/${workOrderId}/route-to-department`, { department_id: departmentId });
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to route work order');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Route to department error:', error);
        throw error;
    }
}
